/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { connect } from 'react-redux';
import { from, Subject } from 'rxjs';
import { retry, map, concatMap } from 'rxjs/operators';
import { SendGAEvent } from 'shared-ui/utils/gaCommon';

import { NotificationActions, NotificationContainer, NotFoundPage } from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import { INVENTORY_ACTIVITY_PASS } from 'ui-field-sales/src/constants/types';
import { INVENTORY_PARKPASS } from 'ui-cart/src/constants';

import ActivityPassDetailsPage from '../components/ActivityPassDetailsPage/ActivityPassDetailsPage';
import { useError, isFacilityActive } from '../utils';

import { fetchFacilityByIdOrSlug, fetchPassTypes, fetchReservations } from '../actions';
import { getParentAsset, getLocationAsset, getFacilityPassTypesOffline } from '../api';
import {
    getErrors,
    facilityByIdOrSlugSelectors,
    passTypesSelectors,
    reservationsSelectors,
    isLoading,
    isLoaded,
    validationSelectors,
} from '../reducers';

import { FacilityProps, PassTypeProps, ReservationProps } from '../props';

const LoadingTypes = PropTypes.shape({
    facility: PropTypes.bool,
    passTypes: PropTypes.bool,
    reservations: PropTypes.bool,
    facilityByIdOrSlug: PropTypes.bool,
});

export class ActivityPassDetailsPageContainer extends Component {
    static defaultProps = {
        fetchFacilityByIdOrSlug: () => {},
        loaded: {},
        loading: {},
        reservations: [],
    };

    static propTypes = {
        cartReservationIds: PropTypes.arrayOf(PropTypes.string),
        facility: FacilityProps,
        fetchFacilityByIdOrSlug: PropTypes.func,
        fetchPassTypes: PropTypes.func,
        fetchReservations: PropTypes.func,
        loaded: LoadingTypes,
        loading: LoadingTypes,
        router: COMPAT_ROUTER_PROP_TYPES,
        passTypes: PropTypes.arrayOf(PassTypeProps),
        reservations: PropTypes.arrayOf(ReservationProps),
        setNotification: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.gaRef = React.createRef();
        this.gaRef.current = false;

        const { siteId } = props.router.params;

        this.priceSubject = new Subject();

        this.state = {
            reservations: props.reservations.filter(
                ({ facility_id: id }) => id === siteId
            ),
            location: [],
            passTypesOffline: [],
        };
    }

    componentDidMount() {
        const { loading, loaded } = this.props;

        const { siteId, resId } = this.props.router.params;
        const searchParams = this.props.router.searchParams;
        const query = Object.fromEntries(searchParams.entries());

        this.loadPageData(loading, loaded, {
            ...query,
            fid: siteId || query.fid,
            rid: resId || query.rid,
        });

        this.priceSubject
            .asObservable()
            .pipe(
                concatMap((reservation) => from(this.getReservationPrice(reservation))),
                retry(5),
                map(({ data }) => data)
            )
            .subscribe((pricing) => {
                const reservations = cloneDeep(this.state.reservations);
                const { id, fees, price, total } = pricing;
                const index = reservations.findIndex(({ id: rId }) => rId === id);
                if (index > -1) {
                    Object.assign(reservations[index], {
                        has_price: true,
                        fees,
                        price,
                        total,
                    });
                    if (!isEqual(this.state.reservations, reservations)) {
                        this.setState({ reservations });
                    }
                }
            });
    }

    componentDidUpdate(prevProps) {
        const { facility } = this.props;
        if (prevProps.facility !== facility) {
            getParentAsset(facility?.parent_asset_id)
                .then(({ data: { results = [] } }) => {
                    this.setState({ parentLocation: results });
                })
                .catch((e) => {
                    this.notifyError(e);
                });

            getLocationAsset(facility?.id)
                .then(({ data: { results = [] } }) => {
                    this.setState({ location: results });
                })
                .catch((e) => {
                    this.notifyError(e);
                });

            getFacilityPassTypesOffline(facility?.id)
                .then(({ data: { pass_types = [] } }) => {
                    this.setState({ passTypesOffline: pass_types });
                })
                .catch((e) => {
                    this.notifyError(e);
                });
        }
    }

    notifyError = (e) => {
        const { setNotification } = this.props;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const err = useError(e, 'There was a problem loading the activity passes.');
        setNotification('error', err);
    };

    getSiteId = () =>
        this.props.router.params.siteId || this.props.router.searchParams.get('fid');

    loadPageData = (loading, loaded, query) => {
        if (!loading.facility && !loaded.facility) {
            const facilityId = this.getSiteId();
            const id = facilityId?.replace(/[^0-9a-zA-Z_-]/g, '');
            this.props
                .fetchFacilityByIdOrSlug(id)
                .then((facility) => {
                    if (facility.asset_id && !loading.passTypes && !loaded.passTypes) {
                        this.props.fetchPassTypes(facility.asset_id);
                    }
                })
                .catch((e) => this.notifyError(e));
        }
        const { facility } = this.props;
        const facilityId = this.getSiteId();
        if (facilityId && !facility) {
            this.props.router.navigate('/pass', { replace: true });
            return;
        }
        const { rid } = query;

        if (
            (rid || this.props.cartReservationIds.length) &&
            !loading.reservations &&
            !loaded.reservations
        ) {
            this.props
                .fetchReservations(
                    query.rid ? [query.rid] : this.props.cartReservationIds
                )
                .catch((e) => this.notifyError(e));
        }
    };

    render() {
        const { facility, loading, loaded, passTypes } = this.props;
        const { parentLocation, location, passTypesOffline } = this.state;

        // Passing additional data to GA
        const wasGaEventFired = this.gaRef;
        const facilityName = facility?.name;
        const facilityId = facility?.id;
        const agencyName = facility?.agency;
        const item_category = INVENTORY_ACTIVITY_PASS;

        // Fire a GA Event to pass the additional info about this page to the GA dataLayer
        if (window.dataLayer && !wasGaEventFired.current && loaded.facilityByIdOrSlug) {
            window.dataLayer.push({
                item_category,
                agencyName,
                facilityId,
                facilityName,
            });
            wasGaEventFired.current = true;
        }

        if (loading?.any === false && facility && !isFacilityActive(facility)) {
            return <NotFoundPage />;
        }

        return (
            <div>
                {/* Fire the view_item GA Event */}
                <SendGAEvent
                    itemLevel="Facility"
                    itemCategory={INVENTORY_ACTIVITY_PASS}
                    facilityId={facilityId}
                />
                <NotificationContainer className={'mb-0 mx-6 mt-4'} />
                <ActivityPassDetailsPage
                    facility={facility}
                    isLoading={!loaded.facilityByIdOrSlug || !loaded.passTypes}
                    passTypes={passTypes}
                    parentLocation={parentLocation}
                    location={location}
                    passTypesOffline={passTypesOffline}
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const errors = getErrors(state, 'reservations', 'facilityByIdOrSlug', 'passTypes');
    const validationErrors = validationSelectors.getValidationErrors(state);

    if (validationErrors.length) {
        errors.validation = validationErrors;
    }

    const reservations = reservationsSelectors.getReservations(state);

    return {
        account: state.auth && state.auth.account && state.auth.account.account,
        cartReservationIds: state.cart.reservations
            .filter(({ inventory_type: type }) => type === INVENTORY_PARKPASS)
            .map(({ reservation_id: id }) => id),
        facility: facilityByIdOrSlugSelectors.getFacilityByIdOrSlug(state),
        loaded: isLoaded(state, 'reservations', 'facilityByIdOrSlug', 'passTypes'),
        loading: isLoading(state, 'reservations', 'facilityByIdOrSlug', 'passTypes'),
        passTypes: passTypesSelectors.getPassTypes(state),
        reservations: Array.isArray(reservations) ? reservations : [reservations],
    };
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            fetchFacilityByIdOrSlug,
            fetchPassTypes,
            fetchReservations,
            setNotification: NotificationActions.setNotification,
        },
        dispatch
    );

export default withCompatRouter(
    connect(mapStateToProps, mapDispatchToProps)(ActivityPassDetailsPageContainer)
);

// cspell:ignore parkpass, ptid
